'use client'

import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { connectFacebookOAuthIntegration } from '@/lib/facebook-oauth-auth'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { Bug, Check, Info, RefreshCw, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaFacebook, FaInstagram } from 'react-icons/fa'
import { toast } from 'react-toastify'
import Modal from '../Modal/Modal'
import { RiveSpinner } from '../ui/RiveSpinner'
import { SearchableDropdown } from '../ui/SearchableDropdown'
import { Button } from '../ui/button'
import { Spinner } from '../ui/spinner'
import { OAuthButton } from './OAuthButton'

type FacebookPage = {
  id: string
  name: string
}

type FacebookAdAccount = {
  id: string
  name: string
}

type InstagramAccount = {
  id: string
  username: string
  name?: string
  profile_picture_url?: string
  pageId: string
  pageName: string
}

type FacebookOAuthModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editMode?: boolean
}

/**
 * Normalize a user-typed ad account ID to the canonical "act_NUMERIC" form.
 * Accepts bare numeric ("123456789") or already-prefixed ("act_123456789").
 * Returns null when the input isn't a valid numeric ID.
 */
function normalizeAdAccountInput(input: string): string | null {
  const trimmed = input.trim()
  const numeric = trimmed.toLowerCase().startsWith('act_') ? trimmed.slice(4) : trimmed
  if (!/^\d+$/.test(numeric)) return null
  return `act_${numeric}`
}

// Permissions we request from users and what they enable
// Based on actual tools in packages/types/src/workflow/tools/facebook*.ts
// Grouped similar features for concise display
const REQUIRED_PERMISSIONS = {
  ads_management: {
    name: 'Ads Management',
    features: ['Create and manage ads, campaigns, and ad sets', 'Manage pixels and audiences'],
  },
  ads_read: {
    name: 'Ads Read',
    features: ['View ad performance and metrics', 'View pixel, audience, and creative data'],
  },
  pages_show_list: {
    name: 'Pages List',
    features: ['View Pages and list lead forms'],
  },
  pages_read_engagement: {
    name: 'Page Engagement',
    features: ['View Page and form engagement data'],
  },
  // Note: pages_manage_ads only requested in test mode
  // Included here so test users can see if they have it
  pages_manage_ads: {
    name: 'Pages Manage Ads',
    features: ['Create lead generation forms'],
  },
}

type PermissionKey = keyof typeof REQUIRED_PERMISSIONS

// Core permissions requested in production OAuth flow
// These are the scopes we ask ALL users for
const PRODUCTION_SCOPES: PermissionKey[] = [
  'ads_management',
  'ads_read',
  'pages_show_list',
  'pages_read_engagement',
]

// Helper to analyze granted permissions
function analyzePermissions(grantedScopes: string[]): {
  allGranted: boolean
  granted: PermissionKey[]
  missing: PermissionKey[]
  canDo: string[]
  cannotDo: string[]
} {
  const requiredKeys = Object.keys(REQUIRED_PERMISSIONS) as PermissionKey[]
  const granted = requiredKeys.filter((key) => grantedScopes.includes(key))
  const missing = requiredKeys.filter((key) => !grantedScopes.includes(key))

  const canDo = granted.flatMap((key) => REQUIRED_PERMISSIONS[key].features)

  // Only show "cannot do" for production scopes that are missing
  // Never show test-only scopes as "cannot do"
  const cannotDo = missing
    .filter((key) => PRODUCTION_SCOPES.includes(key))
    .flatMap((key) => REQUIRED_PERMISSIONS[key].features)

  // Only check production scopes for "all granted" status
  // Test-only scopes (like pages_manage_ads) don't affect this
  const missingProductionScopes = PRODUCTION_SCOPES.filter((key) => !grantedScopes.includes(key))

  return {
    allGranted: missingProductionScopes.length === 0,
    granted,
    missing,
    canDo,
    cannotDo,
  }
}

// Permission Status Indicator Component
function PermissionStatusIndicator({
  grantedScopes,
  className,
}: {
  grantedScopes: string[] | null
  className?: string
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!grantedScopes || grantedScopes.length === 0) {
    return null
  }

  const analysis = analyzePermissions(grantedScopes)
  const isAllGranted = analysis.allGranted

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      })
    }
  }

  const handleMouseEnter = () => {
    updatePosition()
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  const tooltipContent = (
    <AnimatePresence>
      {showTooltip && (
        <motion.div
          initial={{ opacity: 0, y: -5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="fixed z-[9999] w-72"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            maxWidth: 'calc(100vw - 40px)',
          }}
        >
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center gap-2">
              <div
                className={cn('h-2 w-2 rounded-full', {
                  'bg-green-500': isAllGranted,
                  'bg-yellow-500': !isAllGranted,
                })}
              />
              <h4 className="text-sm font-semibold text-gray-900">
                {isAllGranted ? 'All Permissions Granted' : 'Limited Permissions'}
              </h4>
            </div>

            {analysis.canDo.length > 0 && (
              <div className="mb-3">
                <p className="mb-1.5 text-xs font-medium text-gray-700">You can:</p>
                <ul className="space-y-1">
                  {analysis.canDo.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-green-600" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.cannotDo.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-700">You cannot:</p>
                <ul className="space-y-1">
                  {analysis.cannotDo.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <X className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-gray-500">Reconnect to grant missing permissions</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  return (
    <>
      <motion.button
        ref={buttonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-gray-100',
          className
        )}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <Info
          className={cn('h-4 w-4', {
            'text-green-600': isAllGranted,
            'text-yellow-600': !isAllGranted,
          })}
        />
      </motion.button>

      {isMounted && createPortal(tooltipContent, document.body)}
    </>
  )
}

export function FacebookOAuthModal({
  isOpen,
  onClose,
  onSuccess,
  editMode = false,
}: FacebookOAuthModalProps) {
  const [isConnecting, setIsConnecting] = useState(false)
  const [showReconnectSuccess, setShowReconnectSuccess] = useState(false)
  const [showPageSelection, setShowPageSelection] = useState(false)
  const [pages, setPages] = useState<FacebookPage[]>([])
  const [selectedPageId, setSelectedPageId] = useState<string>('')
  const [isLoadingPages, setIsLoadingPages] = useState(false)
  const [adAccounts, setAdAccounts] = useState<FacebookAdAccount[]>([])
  const [selectedAdAccountId, setSelectedAdAccountId] = useState<string>('')
  const [isLoadingAdAccounts, setIsLoadingAdAccounts] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [userName, setUserName] = useState<string>('')
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [showAdAccountSaved, setShowAdAccountSaved] = useState(false)

  const [showPageSaved, setShowPageSaved] = useState(false)
  const [showInstagramSaved, setShowInstagramSaved] = useState(false)

  // Onboarding slide state
  const [isOnboarding, setIsOnboarding] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)

  // Get current business profile ID for API calls
  const currentBusinessProfileId = useCurrentBusinessProfileId()
  const hasChangesRef = useRef(false)

  // Instagram account state
  const [instagramAccounts, setInstagramAccounts] = useState<InstagramAccount[]>([])
  const [selectedInstagramAccountId, setSelectedInstagramAccountId] = useState<string>('')
  const [isLoadingInstagram, setIsLoadingInstagram] = useState(false)
  const [showForceConnectPrompt, setShowForceConnectPrompt] = useState(false)
  const [pendingUsername, setPendingUsername] = useState<string>('')
  const [forceConnectedAccountIds, setForceConnectedAccountIds] = useState<Set<string>>(new Set())

  // Debug token state
  const [facebookDebugData, setFacebookDebugData] = useState<any>(null)
  const [showFacebookDebug, setShowFacebookDebug] = useState(false)
  const [loadingDebugData, setLoadingDebugData] = useState(false)

  // Permission tracking state
  const [grantedScopes, setGrantedScopes] = useState<string[] | null>(null)

  // Search states for dropdowns

  const handlePageSelect = useCallback(
    async (pageId: string) => {
      setSelectedPageId(pageId)

      // Auto-save the selected page
      setIsSaving(true)
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              selectedPageId: pageId,
            }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save page selection')
          return
        }

        hasChangesRef.current = true
        setShowPageSaved(true)
        setTimeout(() => setShowPageSaved(false), 3000)

        // If in onboarding mode, auto-advance to next slide
        if (isOnboarding && currentSlide === 1) {
          setTimeout(() => {
            setCurrentSlide(2)
          }, 500)
        }
      } catch (error) {
        console.error('Error saving page selection:', error)
        toast.error('Failed to save page selection')
      } finally {
        setIsSaving(false)
      }
    },
    [isOnboarding, currentSlide, currentBusinessProfileId]
  )

  const handleCustomPageIdSave = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      setIsSaving(true)
      try {
        // Resolve handle, URL, or numeric ID via the backend (always returns { id, name })
        const searchParams = new URLSearchParams({ handle: text.trim() })
        if (currentBusinessProfileId) {
          searchParams.set('businessProfileId', currentBusinessProfileId)
        }
        const resolveResponse = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/pages/resolve?${searchParams.toString()}`,
          { credentials: 'include' }
        )
        const resolveData = await resolveResponse.json()
        if (!resolveResponse.ok || !resolveData.success) {
          toast.error(
            `Could not find a Facebook Page for "${text.trim()}". Check the handle, URL, or Page ID.`
          )
          return
        }
        const resolvedPageId = resolveData.page.id as string
        const resolvedPageName = resolveData.page.name as string

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selectedPageId: resolvedPageId }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save page')
          return
        }

        // Ensure the resolved page appears in the dropdown list
        if (!pages.find((p) => p.id === resolvedPageId)) {
          setPages((prev) => [...prev, { id: resolvedPageId, name: resolvedPageName }])
        }

        hasChangesRef.current = true
        setSelectedPageId(resolvedPageId)
        setShowPageSaved(true)
        setTimeout(() => setShowPageSaved(false), 3000)

        // If in onboarding mode, auto-advance to next slide
        if (isOnboarding && currentSlide === 1) {
          setTimeout(() => setCurrentSlide(2), 500)
        }
      } catch (error) {
        console.error('Error saving page:', error)
        toast.error('Failed to save page')
      } finally {
        setIsSaving(false)
      }
    },
    [pages, isOnboarding, currentSlide, currentBusinessProfileId]
  )

  const handleInstagramAccountSelect = useCallback(
    async (accountId: string) => {
      setSelectedInstagramAccountId(accountId)

      // Auto-save the selected Instagram account
      setIsSaving(true)
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              selectedInstagramAccountId: accountId,
            }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save Instagram account selection')
          return
        }

        hasChangesRef.current = true
        setShowInstagramSaved(true)
        setTimeout(() => setShowInstagramSaved(false), 3000)

        // If in onboarding mode, complete onboarding after Instagram selection
        if (isOnboarding && currentSlide === 2) {
          setTimeout(() => {
            setIsOnboarding(false)
            setCurrentSlide(0)
          }, 800)
        }
      } catch (error) {
        console.error('Error saving Instagram account selection:', error)
        toast.error('Failed to save Instagram account selection')
      } finally {
        setIsSaving(false)
      }
    },
    [isOnboarding, currentSlide, currentBusinessProfileId]
  )

  const handleCustomInstagramAccountIdSave = useCallback(
    async (text: string) => {
      if (!text.trim()) return

      // If input is not numeric, search the already-loaded instagramAccounts by username
      let resolvedAccountId = text.trim()
      const isForceConnection = false

      if (!/^\d+$/.test(resolvedAccountId)) {
        const username = resolvedAccountId.replace(/^@/, '').toLowerCase()
        const match = instagramAccounts.find((a) => a.username.toLowerCase() === username)

        if (!match) {
          // Show inline prompt for force connection
          setPendingUsername(username)
          setShowForceConnectPrompt(true)
          return
        } else {
          resolvedAccountId = match.id
        }
      }

      setIsSaving(true)
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selectedInstagramAccountId: resolvedAccountId }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save Instagram account')
          return
        }

        hasChangesRef.current = true
        setSelectedInstagramAccountId(resolvedAccountId)
        setShowInstagramSaved(true)
        setTimeout(() => setShowInstagramSaved(false), 3000)

        if (isForceConnection) {
          toast.warning(
            'Instagram account connected with limited permissions. Some features may not work properly.',
            { autoClose: 8000 }
          )
        }

        // If in onboarding mode, complete onboarding after Instagram selection
        if (isOnboarding && currentSlide === 2) {
          setTimeout(() => {
            setIsOnboarding(false)
            setCurrentSlide(0)
          }, 800)
        }
      } catch (error) {
        console.error('Error saving Instagram account:', error)
        toast.error('Failed to save Instagram account')
      } finally {
        setIsSaving(false)
      }
    },
    [instagramAccounts, isOnboarding, currentSlide, currentBusinessProfileId]
  )

  const handleForceConnectConfirm = useCallback(async () => {
    if (!pendingUsername) return

    setShowForceConnectPrompt(false)
    setIsSaving(true)

    try {
      const searchParams = new URLSearchParams({ username: pendingUsername })
      const resolveResponse = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/instagram/resolve-username?${searchParams.toString()}`,
        { credentials: 'include' }
      )

      const resolveData = await resolveResponse.json()

      if (!resolveResponse.ok || !resolveData.success) {
        toast.error(
          `Could not find Instagram account @${pendingUsername}. ${resolveData.error || 'Please check the username and try again.'}`
        )
        setPendingUsername('')
        return
      }

      const resolvedAccountId = resolveData.instagramAccount.id

      console.log(
        `[Instagram Force Connect] Resolved @${pendingUsername} to ID: ${resolvedAccountId}`
      )

      // Save the account
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selectedInstagramAccountId: resolvedAccountId }),
        }
      )

      const data = await response.json()

      if (!response.ok || !data.success) {
        toast.error('Failed to save Instagram account')
        setPendingUsername('')
        return
      }

      hasChangesRef.current = true
      setSelectedInstagramAccountId(resolvedAccountId)
      setShowInstagramSaved(true)
      setTimeout(() => setShowInstagramSaved(false), 3000)

      // Mark this account as force-connected
      setForceConnectedAccountIds((prev) => new Set(prev).add(resolvedAccountId))

      toast.warning(
        'Instagram account connected with limited permissions. Some features may not work properly.',
        { autoClose: 8000 }
      )

      // If in onboarding mode, complete onboarding after Instagram selection
      if (isOnboarding && currentSlide === 2) {
        setTimeout(() => {
          setIsOnboarding(false)
          setCurrentSlide(0)
        }, 800)
      }

      setPendingUsername('')
    } catch (error) {
      console.error('Error resolving Instagram username:', error)
      toast.error('Failed to resolve Instagram username')
      setPendingUsername('')
    } finally {
      setIsSaving(false)
    }
  }, [pendingUsername, currentBusinessProfileId, isOnboarding, currentSlide])

  const handleForceConnectCancel = useCallback(() => {
    setShowForceConnectPrompt(false)
    setPendingUsername('')
  }, [])

  const loadSelectedCredentials = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.credentials) {
          if (data.credentials.selectedPageId) {
            setSelectedPageId(data.credentials.selectedPageId)
          }
          if (data.credentials.selectedAdAccountId) {
            setSelectedAdAccountId(data.credentials.selectedAdAccountId)
          }
          if (data.credentials.selectedInstagramAccountId) {
            setSelectedInstagramAccountId(data.credentials.selectedInstagramAccountId)
          }
        }
        return data.credentials
      }
    } catch (error) {
      console.error('Error loading selected credentials:', error)
    }
    return null
  }, [currentBusinessProfileId])

  const fetchUserInfo = useCallback(async () => {
    setIsLoadingUser(true)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/user${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      // Handle 404 gracefully (no credentials yet)
      if (response.status === 404) {
        console.log('[FacebookOAuthModal] No credentials found yet')
        setUserName('')
        setGrantedScopes(null)
        return
      }

      // Handle 500 (credentials exist but are invalid/expired)
      if (response.status === 500) {
        console.log('[FacebookOAuthModal] Failed to fetch user info, credentials may be invalid')
        toast.error('Failed to connect to Facebook. Please reconnect your account.', {
          autoClose: 5000,
        })
        setUserName('')
        setGrantedScopes(null)
        return
      }

      const data = await response.json()

      if (response.ok && data.success) {
        setUserName(data.user.name)

        // Also fetch debug token data to get granted scopes
        try {
          const debugResponse = await fetch(
            `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/debug-token${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
            {
              credentials: 'include',
            }
          )

          if (debugResponse.ok) {
            const debugData = await debugResponse.json()
            if (debugData.debugData?.user?.data?.scopes) {
              setGrantedScopes(debugData.debugData.user.data.scopes)
            }
          }
        } catch (debugError) {
          console.error('Error fetching debug token data:', debugError)
          // Don't fail the whole operation if debug token fetch fails
        }
      }
    } catch (error) {
      console.error('Error fetching Facebook user info:', error)
      setUserName('')
      setGrantedScopes(null)
    } finally {
      setIsLoadingUser(false)
    }
  }, [currentBusinessProfileId])

  const fetchPages = useCallback(
    async (
      credentials?: {
        selectedPageId?: string
        selectedAdAccountId?: string
        selectedInstagramAccountId?: string
      },
      adAccountId?: string
    ): Promise<boolean> => {
      setIsLoadingPages(true)
      try {
        const searchParams = new URLSearchParams()
        if (currentBusinessProfileId) {
          searchParams.set('businessProfileId', currentBusinessProfileId)
        }

        // Use ad-account-specific endpoint if ad account ID is provided
        const endpoint = adAccountId
          ? `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/ad-accounts/${adAccountId}/pages${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
          : `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/pages${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

        const response = await fetch(endpoint, {
          credentials: 'include',
        })

        // Handle 404 gracefully (no credentials yet)
        if (response.status === 404) {
          console.log('[FacebookOAuthModal] No credentials found, user needs to authenticate first')
          setPages([])
          return false
        }

        // Handle 500 (credentials exist but are invalid/expired)
        if (response.status === 500) {
          console.log('[FacebookOAuthModal] Failed to fetch pages, credentials may be invalid')
          toast.error('Failed to load Facebook pages. Please reconnect your account.', {
            autoClose: 5000,
          })
          setPages([])
          return false
        }

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error(data.error || 'Failed to load Facebook pages')
          return false
        }

        const fetchedPages = data.pages || []
        // Append a synthetic entry for the saved page if it isn't in the fetched list
        const savedPageId = credentials?.selectedPageId
        const pageInList =
          savedPageId && fetchedPages.some((p: FacebookPage) => p.id === savedPageId)
        const finalPages: FacebookPage[] =
          savedPageId && !pageInList
            ? [...fetchedPages, { id: savedPageId as string, name: savedPageId as string }]
            : fetchedPages
        setPages(finalPages)
        return true
      } catch (error) {
        console.error('Error fetching Facebook pages:', error)
        toast.error('Failed to load Facebook pages')
        return false
      } finally {
        setIsLoadingPages(false)
      }
    },
    [currentBusinessProfileId]
  )

  const fetchAdAccounts = useCallback(async () => {
    setIsLoadingAdAccounts(true)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/ad-accounts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      if (response.status === 404) {
        console.log('[FacebookOAuthModal] No credentials found for ad accounts')
        setAdAccounts([])
        return
      }

      if (response.status === 500) {
        console.log('[FacebookOAuthModal] Failed to fetch ad accounts')
        setAdAccounts([])
        return
      }

      const data = await response.json()

      if (!response.ok || !data.success) {
        console.log('[FacebookOAuthModal] Ad accounts fetch failed:', data.error)
        setAdAccounts([])
        return
      }

      setAdAccounts(data.adAccounts || [])
    } catch (error) {
      console.error('Error fetching ad accounts:', error)
      setAdAccounts([])
    } finally {
      setIsLoadingAdAccounts(false)
    }
  }, [currentBusinessProfileId])

  const fetchInstagramAccounts = useCallback(
    async (
      credentials?: {
        selectedPageId?: string
        selectedAdAccountId?: string
        selectedInstagramAccountId?: string
      },
      adAccountId?: string
    ): Promise<boolean> => {
      setIsLoadingInstagram(true)
      try {
        const searchParams = new URLSearchParams()
        if (currentBusinessProfileId) {
          searchParams.set('businessProfileId', currentBusinessProfileId)
        }

        // Use ad-account-specific endpoint if ad account ID is provided
        const endpoint = adAccountId
          ? `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/ad-accounts/${adAccountId}/instagram-accounts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
          : `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/instagram-accounts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

        const response = await fetch(endpoint, {
          credentials: 'include',
        })

        if (response.status === 404) {
          console.log('[FacebookOAuthModal] No credentials found for Instagram accounts')
          setInstagramAccounts([])
          return false
        }

        if (response.status === 500) {
          console.log('[FacebookOAuthModal] Failed to fetch Instagram accounts')
          setInstagramAccounts([])
          return false
        }

        const data = await response.json()

        if (!response.ok || !data.success) {
          console.log('[FacebookOAuthModal] Instagram accounts fetch failed:', data.error)
          setInstagramAccounts([])
          return false
        }

        const fetchedAccounts = data.instagramAccounts || []

        // Append a synthetic entry for the saved account if it isn't in the fetched list
        const savedAccountId = credentials?.selectedInstagramAccountId
        const accountInList =
          savedAccountId && fetchedAccounts.some((a: InstagramAccount) => a.id === savedAccountId)
        const finalAccounts: InstagramAccount[] =
          savedAccountId && !accountInList
            ? [
                ...fetchedAccounts,
                {
                  id: savedAccountId as string,
                  username: savedAccountId as string,
                  pageId: '',
                  pageName: '',
                },
              ]
            : fetchedAccounts
        setInstagramAccounts(finalAccounts)
        return true
      } catch (error) {
        console.error('Error fetching Instagram accounts:', error)
        setInstagramAccounts([])
        return false
      } finally {
        setIsLoadingInstagram(false)
      }
    },
    [currentBusinessProfileId]
  )

  const handleAdAccountSelect = useCallback(
    async (accountId: string) => {
      setIsSaving(true)

      try {
        // Save to backend first before updating UI
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/facebook?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              selectedAdAccountId: accountId,
            }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save ad account selection')
          return
        }

        hasChangesRef.current = true

        // Only update UI state after successful save
        setSelectedAdAccountId(accountId)
        setSelectedPageId('')
        setSelectedInstagramAccountId('')

        const [pagesSuccess, instagramSuccess] = await Promise.all([
          fetchPages(undefined, accountId),
          fetchInstagramAccounts(undefined, accountId),
        ])

        if (pagesSuccess && instagramSuccess) {
          setShowAdAccountSaved(true)
          setTimeout(() => setShowAdAccountSaved(false), 3000)

          // If in onboarding mode, auto-advance to next slide
          if (isOnboarding && currentSlide === 0) {
            setTimeout(() => {
              setCurrentSlide(1)
            }, 500)
          }
        } else if (!pagesSuccess && !instagramSuccess) {
          toast.warning(
            'Ad account saved, but failed to load pages and Instagram accounts. Please refresh or try selecting the ad account again.',
            {
              autoClose: 8000,
            }
          )
        } else if (!pagesSuccess) {
          toast.warning(
            'Ad account saved, but failed to load pages. Instagram accounts loaded successfully.',
            {
              autoClose: 6000,
            }
          )
        } else {
          toast.warning(
            'Ad account saved, but failed to load Instagram accounts. Pages loaded successfully.',
            {
              autoClose: 6000,
            }
          )
        }
      } catch (error) {
        console.error('Error saving ad account selection:', error)
        toast.error('Failed to save ad account selection')
      } finally {
        setIsSaving(false)
      }
    },
    [fetchPages, fetchInstagramAccounts, isOnboarding, currentSlide, currentBusinessProfileId]
  )

  const handleCustomAdAccountSelect = useCallback(
    (query: string) => {
      const normalized = normalizeAdAccountInput(query)
      if (!normalized) {
        toast.error('Enter a valid ad account ID (e.g. 123456789 or act_123456789)')
        return
      }
      void handleAdAccountSelect(normalized)
    },
    [handleAdAccountSelect]
  )

  useEffect(() => {
    if (isOpen) {
      hasChangesRef.current = false
      if (editMode) {
        // In edit mode, fetch existing user info and show page selection
        setShowPageSelection(true)
        void fetchUserInfo()
        void fetchAdAccounts()

        void (async () => {
          const credentials = await loadSelectedCredentials()
          const adAccountId = credentials?.selectedAdAccountId

          // Check if this is first time opening modal in edit mode (credentials exist but no account selected)
          const isFirstTime = !adAccountId

          if (isFirstTime) {
            // Start onboarding flow
            setIsOnboarding(true)
            setCurrentSlide(0)
          } else {
            // Load pages and Instagram accounts for existing setup
            await Promise.all([
              fetchPages(credentials, adAccountId),
              fetchInstagramAccounts(credentials, adAccountId),
            ])
          }
        })()
      } else {
        setShowPageSelection(false)
        setPages([])
        setSelectedPageId('')
        setAdAccounts([])
        setSelectedAdAccountId('')
        setUserName('')
        setInstagramAccounts([])
        setSelectedInstagramAccountId('')
      }
    } else {
      // Reset state when modal closes
      setShowPageSelection(false)
      setPages([])
      setSelectedPageId('')
      setAdAccounts([])
      setSelectedAdAccountId('')
      setUserName('')
      setInstagramAccounts([])
      setSelectedInstagramAccountId('')
      setIsOnboarding(false)
      setCurrentSlide(0)
    }
  }, [
    isOpen,
    editMode,
    fetchUserInfo,
    fetchPages,
    fetchAdAccounts,
    fetchInstagramAccounts,
    loadSelectedCredentials,
  ])

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const success = await connectFacebookOAuthIntegration()

      if (!success) {
        toast.error('Failed to connect to Facebook')
        return
      }

      setShowPageSelection(true)
      await fetchUserInfo()
      await fetchAdAccounts()

      // Check if this is first time opening modal after auth (credentials exist but no account selected)
      const credentials = await loadSelectedCredentials()
      const isFirstTime = !credentials?.selectedAdAccountId

      if (isFirstTime) {
        // Start onboarding flow
        setIsOnboarding(true)
        setCurrentSlide(0)
      } else {
        // Load pages and Instagram accounts for existing setup
        // Backend has automatically preserved selections during reconnect
        await fetchPages(credentials, credentials?.selectedAdAccountId)
        await fetchInstagramAccounts(credentials, credentials?.selectedAdAccountId)
      }
    } catch (error) {
      console.error('Error connecting to Facebook:', error)
      toast.error('Failed to connect to Facebook')
    } finally {
      setIsConnecting(false)
    }
  }

  const handleReconnect = useCallback(async () => {
    // Reset success state when starting new connection
    setShowReconnectSuccess(false)

    // Note: Backend automatically preserves selections during OAuth reconnect
    // No need to store them here - they'll be loaded from backend after OAuth
    try {
      const success = await connectFacebookOAuthIntegration()

      if (success) {
        // Show success animation after successful connection
        setTimeout(() => {
          if (!isConnecting) {
            setShowReconnectSuccess(true)
            setTimeout(() => setShowReconnectSuccess(false), 2000)
          }
        }, 500)
      }
    } catch (error) {
      console.error('Error reconnecting to Facebook:', error)
    }
  }, [isConnecting])

  const handleClose = useCallback(() => {
    if (hasChangesRef.current) {
      onSuccess()
      hasChangesRef.current = false
    }
    onClose()
  }, [onClose, onSuccess])

  const handleSkipSlide = useCallback(() => {
    if (currentSlide === 0) {
      // Skipping account selection - exit onboarding and close modal (nothing to show without account)
      setIsOnboarding(false)
      setCurrentSlide(0)
      handleClose()
    } else if (currentSlide === 1) {
      // Skipping page selection - move to Instagram slide
      setCurrentSlide(2)
    } else if (currentSlide === 2) {
      // Skipping Instagram selection - complete onboarding and show default view
      setIsOnboarding(false)
      setCurrentSlide(0)
    }
  }, [currentSlide, handleClose])

  const handleDebugToggle = async () => {
    if (showFacebookDebug) {
      setShowFacebookDebug(false)
      return
    }

    if (facebookDebugData) {
      setShowFacebookDebug(true)
      return
    }

    setLoadingDebugData(true)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/facebook/debug-token${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      if (response.ok) {
        const data = await response.json()
        setFacebookDebugData(data.debugData)
        setShowFacebookDebug(true)
      } else {
        toast.error('Failed to load debug data')
      }
    } catch (error) {
      console.error('Error loading Facebook debug data:', error)
      toast.error('Failed to load debug data')
    } finally {
      setLoadingDebugData(false)
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="font-hanken-grotesk flex h-full max-h-[90vh] w-full max-w-xl flex-col p-4 sm:p-6">
          <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-4 text-black shadow-xl sm:p-6">
            <AnimatePresence mode="wait" initial={false}>
              {!showPageSelection && !editMode ? (
                <motion.div
                  key="oauth-screen"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="flex h-full flex-col"
                >
                  {/* Fixed Header */}
                  <div className="flex-shrink-0 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h2 className="mb-2 text-2xl font-semibold text-black">
                          Connect to Facebook
                        </h2>
                        <p className="text-base text-black/70">
                          Connect your Facebook account to access ads performance data
                        </p>
                      </div>
                      <motion.button
                        onClick={handleClose}
                        disabled={isConnecting}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                          'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300',
                          {
                            'cursor-not-allowed opacity-50': isConnecting,
                          }
                        )}
                      >
                        <X className="h-5 w-5 text-gray-500" />
                      </motion.button>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <div className="space-y-4">
                      <OAuthButton
                        onClick={handleConnect}
                        disabled={isConnecting}
                        isLoading={isConnecting}
                        icon={<FaFacebook className="h-5 w-5" />}
                      >
                        {isConnecting ? 'Connecting...' : 'Connect with Facebook'}
                      </OAuthButton>
                    </div>
                  </div>
                </motion.div>
              ) : isOnboarding ? (
                <motion.div
                  key="onboarding-screen"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="flex h-full flex-col"
                >
                  {/* Fixed Progress Indicator */}
                  <div className="flex-shrink-0 pb-4">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-blue-600">
                          Step {currentSlide + 1} of 3
                        </span>
                      </div>
                      <button
                        onClick={handleSkipSlide}
                        disabled={isSaving}
                        className="text-sm text-black/50 transition-colors hover:text-black/70 disabled:opacity-50"
                      >
                        Skip
                      </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
                      <motion.div
                        className="h-full bg-blue-600"
                        initial={{ width: '0%' }}
                        animate={{ width: `${((currentSlide + 1) / 3) * 100}%` }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      />
                    </div>
                  </div>

                  {/* Scrollable Slide Content */}
                  <div className="min-h-0 flex-1 overflow-y-auto pb-8">
                    <AnimatePresence mode="wait" initial={false}>
                      {currentSlide === 0 && (
                        <motion.div
                          key="slide-0"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <div className="mb-6 flex-shrink-0">
                            <h2 className="mb-2 text-2xl font-semibold text-black">
                              Select Ad Account
                            </h2>
                            <p className="text-base text-black/70">
                              Choose the Facebook ad account you want to use
                            </p>
                          </div>

                          {isLoadingAdAccounts ? (
                            <div className="flex min-h-[200px] items-center justify-center">
                              <RiveSpinner size={64} />
                            </div>
                          ) : (
                            <div>
                              {userName && (
                                <div className="mb-6 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                                    <FaFacebook className="h-5 w-5 text-blue-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium text-gray-600">
                                      Connected as
                                    </p>
                                    <p className="truncate text-sm font-semibold text-black">
                                      {userName}
                                    </p>
                                  </div>
                                </div>
                              )}

                              <SearchableDropdown
                                items={adAccounts.map((account) => ({
                                  id: account.id,
                                  name: account.name,
                                }))}
                                selectedId={selectedAdAccountId}
                                placeholder="Select an ad account"
                                searchPlaceholder="Search ad accounts..."
                                onSelect={(id) => handleAdAccountSelect(id)}
                                onCustomSelect={handleCustomAdAccountSelect}
                                disabled={isSaving}
                                icon={<FaFacebook className="h-5 w-5 flex-shrink-0" />}
                              />
                            </div>
                          )}
                        </motion.div>
                      )}

                      {currentSlide === 1 && (
                        <motion.div
                          key="slide-1"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <div className="mb-6 flex-shrink-0">
                            <h2 className="mb-2 text-2xl font-semibold text-black">
                              Select Facebook Page
                            </h2>
                            <p className="text-base text-black/70">
                              Choose the Facebook page you want to use for your ads (optional)
                            </p>
                          </div>

                          {isLoadingPages ? (
                            <div className="flex min-h-[200px] items-center justify-center">
                              <RiveSpinner size={64} />
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <SearchableDropdown
                                items={pages.map((page) => ({
                                  id: page.id,
                                  name: page.name,
                                }))}
                                selectedId={selectedPageId}
                                placeholder="Select a page (optional)"
                                searchPlaceholder="Search or paste a URL, handle, or ID..."
                                onSelect={(id) => handlePageSelect(id)}
                                onCustomSelect={(query) => void handleCustomPageIdSave(query)}
                                disabled={isSaving}
                                icon={<FaFacebook className="h-5 w-5 flex-shrink-0" />}
                              />
                            </div>
                          )}
                        </motion.div>
                      )}

                      {currentSlide === 2 && (
                        <motion.div
                          key="slide-2"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <div className="mb-6 flex-shrink-0">
                            <h2 className="mb-2 text-2xl font-semibold text-black">
                              Select Instagram Account
                            </h2>
                            <p className="text-base text-black/70">
                              Choose the Instagram account for your ads (optional)
                            </p>
                          </div>

                          {isLoadingInstagram ? (
                            <div className="flex min-h-[200px] items-center justify-center">
                              <RiveSpinner size={64} />
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <SearchableDropdown
                                items={(() => {
                                  const mappedAccounts = instagramAccounts.map((account) => ({
                                    id: account.id,
                                    name: `@${account.username}`,
                                  }))
                                  // If selected ID is not in the list, add it as an unverified account
                                  if (
                                    selectedInstagramAccountId &&
                                    !instagramAccounts.find(
                                      (a) => a.id === selectedInstagramAccountId
                                    )
                                  ) {
                                    const isForceConnected = forceConnectedAccountIds.has(
                                      selectedInstagramAccountId
                                    )
                                    mappedAccounts.push({
                                      id: selectedInstagramAccountId,
                                      name: isForceConnected
                                        ? `ID: ${selectedInstagramAccountId} (unverified)`
                                        : `ID: ${selectedInstagramAccountId}`,
                                    })
                                  } else if (
                                    selectedInstagramAccountId &&
                                    forceConnectedAccountIds.has(selectedInstagramAccountId)
                                  ) {
                                    // Account is in verified list but was force-connected - mark as unverified
                                    const accountIndex = mappedAccounts.findIndex(
                                      (a) => a.id === selectedInstagramAccountId
                                    )
                                    if (accountIndex >= 0) {
                                      mappedAccounts[accountIndex].name += ' (unverified)'
                                    }
                                  }
                                  return mappedAccounts
                                })()}
                                selectedId={selectedInstagramAccountId}
                                placeholder="Select an account (optional)"
                                searchPlaceholder="Search or paste an @username or ID..."
                                onSelect={(id) => handleInstagramAccountSelect(id)}
                                onCustomSelect={(query) =>
                                  void handleCustomInstagramAccountIdSave(query)
                                }
                                disabled={isSaving}
                                icon={<FaInstagram className="h-5 w-5 flex-shrink-0" />}
                              />

                              {showForceConnectPrompt && (
                                <motion.div
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="rounded-xl border border-orange-200 bg-orange-50 p-4"
                                >
                                  <div className="mb-3">
                                    <h4 className="mb-2 font-semibold text-orange-900">
                                      Force Connect @{pendingUsername}?
                                    </h4>
                                    <p className="mb-2 text-sm text-orange-800">
                                      This account was not found in your connected accounts. This
                                      may be because:
                                    </p>
                                    <ul className="mb-2 ml-4 list-disc text-sm text-orange-800">
                                      <li>
                                        The account doesn&apos;t have the required permissions
                                      </li>
                                      <li>
                                        The account isn&apos;t connected to your Facebook Page
                                      </li>
                                      <li>
                                        The account ID hasn&apos;t been shared with your ad account
                                      </li>
                                    </ul>
                                    <p className="text-sm text-orange-800">
                                      We&apos;ll look up the account ID from Instagram&apos;s public
                                      profile.
                                    </p>
                                    <p className="mt-2 text-sm font-semibold text-orange-900">
                                      Note: Some features may not work without proper permissions.
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={handleForceConnectConfirm}
                                      disabled={isSaving}
                                      className="flex-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                                    >
                                      {isSaving ? 'Connecting...' : 'Force Connect'}
                                    </button>
                                    <button
                                      onClick={handleForceConnectCancel}
                                      disabled={isSaving}
                                      className="flex-1 rounded-lg border border-orange-300 bg-white px-4 py-2 text-sm font-medium text-orange-900 hover:bg-orange-50 disabled:opacity-50"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </motion.div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ) : (
                <>
                  {/* Fixed Header - Like IntegrationsModal */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="mb-2 text-2xl font-semibold text-black">
                        {editMode ? 'Edit Facebook Connection' : 'Configure Facebook Ads'}
                      </h2>
                      <p className="mb-4 text-base text-black/70">
                        {editMode
                          ? 'Update your ad account, page, and Instagram account'
                          : 'Select your ad account, page, and Instagram account for your ads'}
                      </p>
                    </div>
                    <button
                      onClick={handleClose}
                      disabled={isSaving}
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                        'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300',
                        {
                          'cursor-not-allowed opacity-50': isSaving,
                        }
                      )}
                    >
                      <X className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>

                  {/* Scrollable Content Area - Only Content */}
                  {isLoadingUser || isLoadingPages || isLoadingAdAccounts ? (
                    <div className="flex min-h-[300px] items-center justify-center">
                      <Spinner size={32} />
                    </div>
                  ) : (
                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-2">
                      <div className="space-y-6">
                        {userName && (
                          <div className="mb-6 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                              <FaFacebook className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-medium text-gray-600">Connected as</p>
                                <PermissionStatusIndicator grantedScopes={grantedScopes} />
                              </div>
                              <p className="truncate text-sm font-semibold text-black">
                                {userName}
                              </p>
                            </div>
                            <motion.button
                              onClick={handleReconnect}
                              disabled={isConnecting || showReconnectSuccess}
                              className={cn(
                                'relative shrink-0 overflow-hidden rounded-lg px-4 py-1.5',
                                'text-xs font-medium text-white',
                                'transition-all duration-300 ease-out',
                                !isConnecting &&
                                  !showReconnectSuccess &&
                                  'hover:shadow-[0_0_20px_rgba(59,130,246,0.15)]',
                                showReconnectSuccess
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-[0_2px_10px_rgba(34,197,94,0.2)]'
                                  : 'bg-gradient-to-r from-blue-600 to-blue-500 shadow-[0_2px_8px_rgba(59,130,246,0.15)]'
                              )}
                              whileHover={
                                !isConnecting && !showReconnectSuccess
                                  ? {
                                      scale: 1.03,
                                      transition: { type: 'spring', stiffness: 400, damping: 17 },
                                    }
                                  : {}
                              }
                              whileTap={
                                !isConnecting && !showReconnectSuccess
                                  ? {
                                      scale: 0.97,
                                      transition: { type: 'spring', stiffness: 400, damping: 17 },
                                    }
                                  : {}
                              }
                            >
                              {/* Subtle shimmer effect on hover */}
                              <motion.div
                                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent"
                                animate={
                                  !isConnecting && !showReconnectSuccess
                                    ? { x: ['-100%', '200%'] }
                                    : {}
                                }
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  repeatDelay: 3,
                                  ease: 'easeInOut',
                                }}
                              />

                              {/* Button content with smooth transitions */}
                              <motion.div
                                className="relative flex items-center justify-center gap-1.5"
                                animate={isConnecting ? { opacity: [1, 0.7, 1] } : {}}
                                transition={
                                  isConnecting
                                    ? { duration: 2, repeat: Infinity, ease: 'easeInOut' }
                                    : {}
                                }
                              >
                                <AnimatePresence mode="wait">
                                  {showReconnectSuccess ? (
                                    <motion.div
                                      key="success"
                                      initial={{ scale: 0, rotate: -180 }}
                                      animate={{ scale: 1, rotate: 0 }}
                                      exit={{ scale: 0, rotate: 180 }}
                                      transition={{ type: 'spring', stiffness: 350, damping: 20 }}
                                      className="flex items-center gap-1.5"
                                    >
                                      <Check className="h-3 w-3" strokeWidth={2.5} />
                                      <span className="font-semibold">Connected</span>
                                    </motion.div>
                                  ) : isConnecting ? (
                                    <motion.div
                                      key="loading"
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: -10 }}
                                      transition={{ duration: 0.2 }}
                                      className="flex items-center gap-1.5"
                                    >
                                      <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                          duration: 1.5,
                                          repeat: Infinity,
                                          ease: 'linear',
                                        }}
                                      >
                                        <RefreshCw className="h-3 w-3" strokeWidth={2} />
                                      </motion.div>
                                      <span>Reconnecting</span>
                                    </motion.div>
                                  ) : (
                                    <motion.div
                                      key="default"
                                      initial={{ opacity: 0, y: -10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, y: 10 }}
                                      transition={{ duration: 0.2 }}
                                      className="flex items-center gap-1.5"
                                    >
                                      <motion.div
                                        whileHover={{ rotate: 180 }}
                                        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                                      >
                                        <RefreshCw className="h-3 w-3" strokeWidth={2} />
                                      </motion.div>
                                      <span className="font-medium">Reconnect</span>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            </motion.button>
                          </div>
                        )}

                        {/* Ad Account Selection */}
                        <div>
                          <div className="mb-1 flex min-h-[20px] items-center gap-2">
                            <label className="text-sm font-medium text-black">
                              Facebook Ad Account
                            </label>
                            <AnimatePresence>
                              {showAdAccountSaved && (
                                <motion.div
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5"
                                >
                                  <Check className="h-3 w-3 text-green-600" />
                                  <span className="text-xs font-medium text-green-700">Saved</span>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                          <p className="mb-2 text-sm text-black/60">
                            Select the ad account you want to use for your ads
                          </p>

                          <SearchableDropdown
                            items={adAccounts}
                            selectedId={selectedAdAccountId}
                            onSelect={handleAdAccountSelect}
                            onCustomSelect={handleCustomAdAccountSelect}
                            placeholder="None"
                            searchPlaceholder="Search ad accounts..."
                            icon={<FaFacebook />}
                            disabled={isSaving}
                            selectedColor="blue"
                          />
                        </div>

                        {/* Helper message when no ad account is selected */}
                        {!selectedAdAccountId && (
                          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                            <p className="text-sm text-blue-800">
                              <span className="font-medium">Select an ad account above</span> to
                              view and configure Facebook Pages and Instagram accounts.
                            </p>
                          </div>
                        )}

                        {/* Facebook Page Selection - Only show when ad account is selected */}
                        {selectedAdAccountId && (
                          <div>
                            <div className="mb-1 flex min-h-[20px] items-center gap-2">
                              <label className="text-sm font-medium text-black">
                                Facebook Page
                              </label>
                              <AnimatePresence>
                                {showPageSaved && (
                                  <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5"
                                  >
                                    <Check className="h-3 w-3 text-green-600" />
                                    <span className="text-xs font-medium text-green-700">
                                      Saved
                                    </span>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                            <p className="mb-2 text-sm text-black/60">
                              Select the page you want to use for your ads
                            </p>

                            <SearchableDropdown
                              items={pages}
                              selectedId={selectedPageId}
                              onSelect={handlePageSelect}
                              onCustomSelect={(query) => void handleCustomPageIdSave(query)}
                              placeholder="None"
                              searchPlaceholder="Search or paste a URL, handle, or ID..."
                              icon={<FaFacebook />}
                              disabled={isSaving}
                              selectedColor="blue"
                            />
                          </div>
                        )}

                        {/* Instagram Account Selection - Only show when ad account is selected */}
                        {selectedAdAccountId && (
                          <div>
                            <div className="mb-1 flex min-h-[20px] items-center gap-2">
                              <label className="text-sm font-medium text-black">
                                Instagram Account
                              </label>
                              <AnimatePresence>
                                {showInstagramSaved && (
                                  <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5"
                                  >
                                    <Check className="h-3 w-3 text-green-600" />
                                    <span className="text-xs font-medium text-green-700">
                                      Saved
                                    </span>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                            <p className="mb-2 text-sm text-black/60">
                              Select the Instagram account for your ads
                            </p>

                            {isLoadingInstagram ? (
                              <div className="flex items-center justify-center rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4">
                                <RiveSpinner size={24} />
                                <span className="ml-2 text-sm text-gray-500">
                                  Loading Instagram accounts...
                                </span>
                              </div>
                            ) : (
                              <>
                                <SearchableDropdown
                                  items={(() => {
                                    const mappedAccounts = instagramAccounts.map((account) => ({
                                      id: account.id,
                                      name: `@${account.username}`,
                                    }))
                                    // If selected ID is not in the list, add it as an unverified account
                                    if (
                                      selectedInstagramAccountId &&
                                      !instagramAccounts.find(
                                        (a) => a.id === selectedInstagramAccountId
                                      )
                                    ) {
                                      const isForceConnected = forceConnectedAccountIds.has(
                                        selectedInstagramAccountId
                                      )
                                      mappedAccounts.push({
                                        id: selectedInstagramAccountId,
                                        name: isForceConnected
                                          ? `ID: ${selectedInstagramAccountId} (unverified)`
                                          : `ID: ${selectedInstagramAccountId}`,
                                      })
                                    } else if (
                                      selectedInstagramAccountId &&
                                      forceConnectedAccountIds.has(selectedInstagramAccountId)
                                    ) {
                                      // Account is in verified list but was force-connected - mark as unverified
                                      const accountIndex = mappedAccounts.findIndex(
                                        (a) => a.id === selectedInstagramAccountId
                                      )
                                      if (accountIndex >= 0) {
                                        mappedAccounts[accountIndex].name += ' (unverified)'
                                      }
                                    }
                                    return mappedAccounts
                                  })()}
                                  selectedId={selectedInstagramAccountId}
                                  placeholder="None"
                                  searchPlaceholder="Search or paste an @username or ID..."
                                  onSelect={(id) => handleInstagramAccountSelect(id)}
                                  onCustomSelect={(query) =>
                                    void handleCustomInstagramAccountIdSave(query)
                                  }
                                  disabled={isSaving}
                                  icon={<FaInstagram className="h-5 w-5 flex-shrink-0" />}
                                />

                                {showForceConnectPrompt && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-4"
                                  >
                                    <div className="mb-3">
                                      <h4 className="mb-2 font-semibold text-orange-900">
                                        Force Connect @{pendingUsername}?
                                      </h4>
                                      <p className="mb-2 text-sm text-orange-800">
                                        This account was not found in your connected accounts. This
                                        may be because:
                                      </p>
                                      <ul className="mb-2 ml-4 list-disc text-sm text-orange-800">
                                        <li>
                                          The account doesn&apos;t have the required permissions
                                        </li>
                                        <li>
                                          The account isn&apos;t connected to your Facebook Page
                                        </li>
                                        <li>
                                          The account ID hasn&apos;t been shared with your ad
                                          account
                                        </li>
                                      </ul>
                                      <p className="text-sm text-orange-800">
                                        We&apos;ll look up the account ID from Instagram&apos;s
                                        public profile.
                                      </p>
                                      <p className="mt-2 text-sm font-semibold text-orange-900">
                                        Note: Some features may not work without proper permissions.
                                      </p>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={handleForceConnectConfirm}
                                        disabled={isSaving}
                                        className="flex-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                                      >
                                        {isSaving ? 'Connecting...' : 'Force Connect'}
                                      </button>
                                      <button
                                        onClick={handleForceConnectCancel}
                                        disabled={isSaving}
                                        className="flex-1 rounded-lg border border-orange-300 bg-white px-4 py-2 text-sm font-medium text-orange-900 hover:bg-orange-50 disabled:opacity-50"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </motion.div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Help & Support Section */}
                        <div className="mt-8 rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-900">
                              Need Help?
                            </p>
                            <button
                              onClick={handleDebugToggle}
                              className="flex items-center justify-center text-sm transition-colors hover:scale-110"
                              disabled={loadingDebugData || isSaving}
                              title={showFacebookDebug ? 'Hide debug info' : 'Show debug info'}
                            >
                              {loadingDebugData ? (
                                <RiveSpinner size={14} />
                              ) : (
                                <span className="text-base">{showFacebookDebug ? '🐛' : '🐛'}</span>
                              )}
                            </button>
                          </div>
                          <div className="space-y-2">
                            <a
                              href="https://sprites-ai.notion.site/Connecting-Your-Facebook-Page-or-Instagram-Account-to-Your-Ad-Account-2f55daa5843b803f8574df05337df1c9"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-700 transition-colors hover:text-blue-900"
                            >
                              <span className="text-base">📖</span>
                              <span className="underline">
                                Troubleshooting guide for Facebook & Instagram connections
                              </span>
                            </a>
                            <a
                              href="https://calendly.com/spritesai/sprites-support"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-700 transition-colors hover:text-blue-900"
                            >
                              <span className="text-base">💬</span>
                              <span className="underline">Book a support call</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fixed Done Button Footer - Like IntegrationsModal */}
                  <div className="mt-4 flex flex-shrink-0 justify-end">
                    <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
                      {isSaving ? <RiveSpinner size={16} /> : 'Done'}
                    </Button>
                  </div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Modal>

      {/* Debug Data Modal */}
      {showFacebookDebug && facebookDebugData && (
        <Modal isOpen={showFacebookDebug} onClose={() => setShowFacebookDebug(false)}>
          <div className="font-hanken-grotesk flex h-full max-h-[90vh] w-[75vw] max-w-5xl flex-col p-4 sm:p-6">
            <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-3xl bg-white text-black shadow-xl">
              {/* Fixed Header */}
              <div className="flex-shrink-0 p-6 pb-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-semibold text-black">Facebook Token Debug Data</h2>
                  <button
                    onClick={() => setShowFacebookDebug(false)}
                    className="rounded-full p-2 transition-colors hover:bg-gray-100"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6">
                {/* User Access Token Debug */}
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <Bug className="h-5 w-5 text-blue-600" />
                    <h3 className="text-base font-semibold text-blue-900">User Access Token</h3>
                  </div>
                  <pre className="overflow-auto rounded-lg bg-white p-4 text-xs text-gray-800">
                    {JSON.stringify(facebookDebugData.user, null, 2)}
                  </pre>
                </div>

                {/* Page Access Token Debug (if available) */}
                {facebookDebugData.page && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Bug className="h-5 w-5 text-purple-600" />
                      <h3 className="text-base font-semibold text-purple-900">Page Access Token</h3>
                    </div>
                    <pre className="overflow-auto rounded-lg bg-white p-4 text-xs text-gray-800">
                      {JSON.stringify(facebookDebugData.page, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {/* Fixed Footer */}
              <div className="flex flex-shrink-0 justify-end p-6 pt-4">
                <button
                  onClick={() => setShowFacebookDebug(false)}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
